import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { updatePreferences } from 'lib/context/TableContext/helper';

import Button from '../../../Button/Button';
import DisplayButton from '../../../DisplayButton/DisplayButton';
import Divider from '../../../Divider/Divider';
import Icon from '../../../Icon/Icon';
import Typography from '../../../Typography/Typography';

import GroupOptions from './GroupOptions';
import OrderOptions from './OrderOptions';
import SubGroupOptions from './SubGroupOptions';

const DisplayButtonAGTable = ({ btnClassName = '', color = 'neutral', textClassName = '' }) => {
  const {
    limit,
    setLimit,
    setColDefs,
    colDefs,
    defaultColumns,
    name,
    pagination,
    setPage,
    gridApi,
    gridRef
  } = useTableContext();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onDragEnd = async (items) => {
    setColDefs(items);
    await updatePreferences({ navigate, cols: items, limit, queryClient, name });
  };

  const togglePinned = async (direction, colIndex) => {
    const newColDefs = colDefs.map((col, index) => {
      if (index === colIndex) {
        if (col.pinned === direction) {
          col.pinned = false;
        } else {
          col.pinned = direction;
        }
      }
      return col;
    });
    setColDefs(newColDefs);
    await updatePreferences({ navigate, cols: newColDefs, limit, queryClient, name });
    queryClient.invalidateQueries(['table-limit']);
  };

  const handleChangeLimit = async (item) => {
    setLimit(item);
    setPage(1);
    await updatePreferences({ navigate, cols: colDefs, limit: item, queryClient, name });
    queryClient.invalidateQueries(['table-limit']);
  };

  const resetSortingAndGrouping = async () => {
    const columnApi =
      gridApi?.getColumnApi?.() ||
      (typeof gridRef?.current?.columnApi !== 'undefined' ? gridRef.current.columnApi : null);

    if (gridApi?.applyColumnState) {
      gridApi.applyColumnState({
        defaultState: {
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pinned: null,
          hide: false
        },
        state: []
      });
    }

    if (columnApi?.getColumns && columnApi?.setColumnsVisible) {
      const allIds = columnApi.getColumns().map((c) => c.getColId());
      columnApi.setColumnsVisible(allIds, true);
      if (columnApi?.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { pinned: null } });
      }
    }

    const newColDefs = defaultColumns.map((c) => {
      const isAction = c.headerName === 'Actions';
      const pinned = isAction ? 'right' : false;
      return {
        ...c,
        sort: null,
        sortIndex: null,
        rowGroup: false,
        rowGroupIndex: null,
        pinned,
        hide: false
      };
    });
    setColDefs(newColDefs);
    await updatePreferences({ navigate, cols: newColDefs, limit, queryClient, name });
  };

  const autosizeAllColumns = () => {
    const columnApi =
      gridApi?.getColumnApi?.() ||
      (typeof gridRef?.current?.columnApi !== 'undefined' ? gridRef.current.columnApi : null);

    if (columnApi?.autoSizeAllColumns) {
      columnApi.autoSizeAllColumns();
      return;
    }

    if (gridApi?.autoSizeAllColumns) {
      gridApi.autoSizeAllColumns();
      return;
    }

    if (gridApi?.autoSizeColumns && gridApi?.getDisplayedCenterColumns) {
      const ids = gridApi.getDisplayedCenterColumns().map((c) => c.getColId());
      gridApi.autoSizeColumns(ids);
    }
  };

  return (
    <DisplayButton
      columns={colDefs}
      pagination={pagination}
      onLimitChange={handleChangeLimit}
      onPinChange={togglePinned}
      onDragEnd={onDragEnd}
      onColumnChange={setColDefs}
      btnClassName={btnClassName}
      color={color}
      textClassName={textClassName}
      disabledActions={['left']}
      disabledItems={['actions']}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1 px-4 py-2">
          <div className="flex items-center justify-between gap-2">
            <Typography variant="label" size="small" weight="semibold">
              Display options
            </Typography>
            <Button size="xs" text="Reset all" variant="link" onClick={resetSortingAndGrouping} />
          </div>
          <GroupOptions />
          <SubGroupOptions />
          <OrderOptions />
        </div>
        <Divider />
        <div className="flex gap-1 px-4 py-2 hover:bg-neutral-100" onClick={autosizeAllColumns}>
          <Icon icon="resize-horizontally-mono" color={color} shade={600} />
          <Typography variant="label" size="medium">
            Autosize all columns
          </Typography>
        </div>
      </div>
    </DisplayButton>
  );
};

export default DisplayButtonAGTable;
